import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from '../../icons';
import { EmptyState, StatTile, StatTiles, Table, type TableColumn } from '../../components/shared';
import { formatWon } from '../../api/hr-wage';
import { formatWorkMinutes } from '../../lib/daily-work';
import {
  getSubstituteSettlement,
  type SubstituteSettlementDto, type SubstituteSettlementRowDto,
} from '../../api/hr-substitute';
import './Substitute.css';

/**
 * 휴일대체 › **소멸 정산** 탭 (2026-09-06 · 판 C).
 *
 * ## ★★★ 왜 있어야 하나
 *
 * §57 은 「임금 지급을 **갈음**하여」 휴가를 주는 것이다. 그 휴가를 **기한 안에 못 쓰면
 * 원래 줬어야 할 임금이 남아 있는 것**이고, 안 주면 위반이다. 그런데 그 몫은 어디에도
 * 안 적혀 있다 — 쓴 것은 「대체휴가 하루」로만 남아 **어느 적립분에서 뺐는지 모른다.**
 * 서버가 배분해 보고, 이 화면이 그 목록을 담당자에게 보인다.
 *
 * ## ★★ 지급하지 않는다
 *
 * 여기는 **얼마를 넘길지 세어 보여 주는 데까지**다(퇴사 정산과 같은 가름). 지급 사실을 여기에
 * 또 적으면 급여와 둘로 갈라진다 — **마이그레이션 0 · 새 표 0.**
 *
 * ## 정본
 * **D9-2 요약 타일** · **E1 Table** · P2-1 「반영 시점의 단위는 탭」 —
 * 이 탭에는 **저장바가 없다**(고칠 것이 없는 읽기 화면이다).
 */

type Load = 'loading' | 'ok' | 'failed' | 'forbidden';

export function SubstituteSettlementTab(): JSX.Element {
  const [data, setData] = useState<SubstituteSettlementDto | null>(null);
  const [state, setState] = useState<Load>('loading');

  const load = useCallback(() => {
    setState('loading');
    getSubstituteSettlement()
      .then((d) => { setData(d); setState('ok'); })
      .catch((err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        setState(status === 403 ? 'forbidden' : 'failed');
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const cols: ReadonlyArray<TableColumn<SubstituteSettlementRowDto>> = useMemo(() => [
    {
      key: 'name',
      header: '이름',
      width: '180px',
      rowHeader: true,
      render: (r) => (
        <>
          {r.name}
          {r.employeeNumber ? <span className="wcsub__dim"> · {r.employeeNumber}</span> : null}
        </>
      ),
    },
    {
      key: 'expired',
      header: '소멸',
      width: '110px',
      render: (r) => <span className="wcsub__strong">{r.expiredText}</span>,
    },
    {
      key: 'when',
      header: '어느 적립분인가',
      render: (r) => (
        <span className="wcsub__dim">
          {r.expiredRows.map((e) => `${e.workDate} 근로 ${e.leftText} (${e.expiresAt ?? '기한 없음'} 만료)`).join(' · ')}
        </span>
      ),
    },
    {
      key: 'live',
      header: '아직 남음',
      width: '110px',
      render: (r) => (r.liveMinutes > 0
        ? <span className="wcsub__dim">{formatWorkMinutes(r.liveMinutes)}</span>
        : <span className="wcsub__dim">—</span>),
    },
    {
      key: 'amount',
      header: '돌려줄 임금(예상)',
      width: '160px',
      align: 'right',
      render: (r) => (r.amount === null
        // ★ 0 원이 아니라 «모른다» 다 — 0 을 주면 못 준 돈이 조용히 사라진다.
        ? <span className="wcsub__warn">임금 정보 없음</span>
        : <span className="wcsub__strong">{formatWon(r.amount)}</span>),
    },
  ], []);

  if (state === 'loading') return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;
  if (state === 'forbidden') {
    return (
      <div className="wc-set__body">
        <p className="wc-set__bad" role="alert">근태 담당자만 볼 수 있습니다 — 남의 임금이 함께 보이는 화면입니다.</p>
      </div>
    );
  }
  if (state === 'failed' || !data) {
    return (
      <div className="wc-set__body">
        <p className="wc-set__bad" role="alert">불러오지 못했습니다.</p>
      </div>
    );
  }

  const { total } = data;

  return (
    <div className="wc-set__body">
      <StatTiles>
        <StatTile label="소멸한 사람" value={total.people} note={`${data.asOf} 기준`} />
        <StatTile
          label="소멸 시간"
          value={total.expiredMinutes > 0 ? formatWorkMinutes(total.expiredMinutes) : '—'}
          tone={total.expiredMinutes > 0 ? 'warning' : 'default'}
        />
        {/*
          ★★ 「합계가 거짓말하지 않게」 — 임금 정보가 없어 못 낸 사람 수를 함께 적는다
             (연장 정산·퇴사 정산과 같은 규칙).
        */}
        <StatTile
          label="돌려줄 임금(예상)"
          value={formatWon(total.amount)}
          note={total.unknownWage > 0 ? `${total.unknownWage}명 제외` : undefined}
          tone={total.unknownWage > 0 ? 'warning' : 'default'}
        />
      </StatTiles>

      <p className="wcsub__note">
        기한이 지나도록 쓰지 않은 보상휴가는 <b>임금으로 돌려주어야 합니다</b> —
        §57 이 「임금 지급을 <b>갈음</b>하여」이므로, 휴가도 안 주고 임금도 안 주면 위반입니다.
        {' '}<b>쓴 것은 먼저 만료되는 적립분부터</b> 뺀 것으로 봅니다(공연한 소멸을 만들지 않기 위해서고,
        그때 이미 기한이 지난 적립분에는 갖다 붙이지 않습니다).
        {' '}이 화면은 <b>얼마를 넘길지 세어 보일 뿐 지급하지 않습니다</b> — 지급은 급여의 몫입니다.
      </p>

      {data.rows.length === 0 ? (
        <EmptyState
          icon={AlertCircle}
          title="기한이 지나 못 쓴 보상휴가가 없습니다."
          description={data.policy.enabled
            ? '적립된 몫은 모두 아직 쓸 수 있거나 이미 쓰였습니다.'
            : '이 회사는 보상휴가를 쓰지 않습니다 — 「정책」 탭에서 켜면 적립이 시작됩니다.'}
        />
      ) : (
        <Table
          caption={`${data.asOf} 기준 소멸 정산`}
          columns={cols}
          rows={data.rows}
          rowKey={(r) => r.userId}
        />
      )}
    </div>
  );
}
